import React from 'react';
import { Link } from 'react-router-dom';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, PageHeader, StatusBadge } from '../../components/ui';
import { assignmentGrade, getCourse, getLatestExerciseSubmission } from '../../utils/lmsSelectors';

export default function LearnerGrades() {
  const { data, currentUser } = useLms();
  const assignments = data.assignments.filter((assignment) => assignment.assignedToUserId === currentUser.id && assignment.status !== 'revoked');
  const rows = assignments.flatMap((assignment) => {
    const course = getCourse(data, assignment.courseId);
    const courseQuizzes = data.quizzes.filter((quiz) => quiz.courseVersionId === assignment.courseVersionId);
    const courseExercises = (data.exercises || []).filter((exercise) => exercise.courseVersionId === assignment.courseVersionId);
    const quizRows = courseQuizzes.map((quiz) => {
      const attempts = assignment.quizAttempts.filter((attempt) => attempt.quizId === quiz.id);
      const best = attempts.length ? Math.max(...attempts.map((attempt) => attempt.scorePercent)) : null;
      const passed = attempts.some((attempt) => attempt.status === 'passed');
      return { id: `${assignment.id}-${quiz.id}`, assignmentId: assignment.id, course: course?.title, item: quiz.title, itemType: 'Quiz', itemPath: `/learner/quiz/${assignment.id}:${quiz.id}`, attempts: attempts.length, score: best, status: passed ? 'passed' : attempts.length ? 'failed' : 'not_started' };
    });
    const exerciseRows = courseExercises.map((exercise) => {
      const submissions = assignment.exerciseSubmissions?.filter((submission) => submission.exerciseId === exercise.id) || [];
      const best = submissions.filter((submission) => submission.scorePercent !== null && submission.scorePercent !== undefined).length
        ? Math.max(...submissions.filter((submission) => submission.scorePercent !== null && submission.scorePercent !== undefined).map((submission) => submission.scorePercent))
        : null;
      const latest = getLatestExerciseSubmission(assignment, exercise.id);
      const passed = submissions.some((submission) => submission.status === 'passed');
      return { id: `${assignment.id}-${exercise.id}`, assignmentId: assignment.id, course: course?.title, item: exercise.title, itemType: 'Exercise', itemPath: `/learner/exercise/${assignment.id}:${exercise.id}`, attempts: submissions.length, score: best, status: passed ? 'passed' : latest?.status || 'not_started' };
    });
    if (!quizRows.length && !exerciseRows.length) {
      return [{ id: assignment.id, assignmentId: assignment.id, course: course?.title, item: 'No quiz or exercise required', itemType: 'Course', itemPath: '', attempts: 0, score: assignmentGrade(data, assignment), status: assignment.status }];
    }
    return [...quizRows, ...exerciseRows];
  });

  return (
    <>
      <PageHeader title="My grades" description="Quiz attempts, graded exercises, and course grade summary for your own assignments only." />
      <Card>
        <DataTable
          rows={rows}
          columns={[
            { key: 'course', label: 'Course', render: (row) => <Link className="text-link strong-link" to={`/learner/course/${row.assignmentId}`}>{row.course}</Link> },
            { key: 'item', label: 'Quiz / Exercise', render: (row) => row.itemPath ? <Link className="text-link strong-link" to={row.itemPath}>{row.item}</Link> : row.item },
            { key: 'itemType', label: 'Type' },
            { key: 'attempts', label: 'Attempts' },
            { key: 'score', label: 'Best score', render: (row) => row.score === null ? '—' : `${row.score}%` },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> }
          ]}
          emptyMessage="No grade records yet."
        />
      </Card>
    </>
  );
}
